import { FC, useCallback, useContext, useEffect, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import { Button, H2, Modal, Paragraph, Preloader } from '@smartshell/ui-kit';
import cn from 'classnames';
import { truncate } from 'lodash';
import { SubmitHandler, useForm } from 'react-hook-form';

import { useSendShellCommand } from 'hooks';
import { LocalizationContext } from 'localization';
import {
  CREATE_HOST_COMMAND_TEMPLATE,
  DELETE_HOST_COMMAND_TEMPLATE,
  GET_HOST_COMMAND_TEMPLATES,
  ICreateHostCommandTemplateRequest,
  ICreateHostCommandTemplateResponse,
  IDeleteHostCommandTemplateRequest,
  IGetHostCommandTemplates,
  IHostCommandTemplate,
  IUpdateHostCommandTemplateRequest,
  IUpdateHostCommandTemplateResponse,
  UPDATE_HOST_COMMAND_TEMPLATE,
} from 'schemas';
import notification from 'services/notificationService';

import { DEFAULT_FORM_DATA } from './constants';
import { IFormValues } from './interfaces';
import RunFileForm from './RunFileForm';
import s from './RunFileModal.module.scss';

interface IRunFileModalProps {
  hostsIds: Array<number>;
  onClose: VoidFunction;
}
export const RunFileModal: FC<IRunFileModalProps> = ({ hostsIds, onClose }) => {
  const { t } = useContext(LocalizationContext);
  const [currentHostCommandTemplate, setCurrentHostCommandTemplate] =
    useState<IHostCommandTemplate | null>(null);
  const [deletedHostCommandTemplate, setDeletedHostCommandTemplate] =
    useState<Pick<Required<IHostCommandTemplate>, 'id' | 'title'> | null>(null);

  const closeDeleteModal = (): void => setDeletedHostCommandTemplate(null);

  const clearCurrentHostCommandTemplate = (): void =>
    setCurrentHostCommandTemplate(null);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { isSubmitting, isDirty },
  } = useForm<IFormValues>({
    mode: 'onChange',
    defaultValues: DEFAULT_FORM_DATA,
  });

  const {
    data: hostCommandTemplates,
    loading: hostCommandTemplatesLoading,
    refetch: refetchGetHostCommandTemplates,
  } = useQuery<IGetHostCommandTemplates>(GET_HOST_COMMAND_TEMPLATES, {
    fetchPolicy: 'no-cache',
  });

  const [createHostCommandTemplate] = useMutation<
    ICreateHostCommandTemplateResponse,
    ICreateHostCommandTemplateRequest
  >(CREATE_HOST_COMMAND_TEMPLATE);

  const [updateHostCommandTemplate] = useMutation<
    IUpdateHostCommandTemplateResponse,
    IUpdateHostCommandTemplateRequest
  >(UPDATE_HOST_COMMAND_TEMPLATE);

  const [deleteHostCommandTemplate] = useMutation<
    unknown,
    IDeleteHostCommandTemplateRequest
  >(DELETE_HOST_COMMAND_TEMPLATE, {
    onCompleted: () => {
      setDeletedHostCommandTemplate(null);
      return refetchGetHostCommandTemplates();
    },
    onError: () => {
      setDeletedHostCommandTemplate(null);
      notification.info({
        t,
        type: 'error',
        title: t('host.notify.commands.template.title'),
      });
    },
  });

  const { sendCommand, sendCommands, createCommandError } =
    useSendShellCommand();

  const sendCommandHandler = useCallback(
    (param: string) => {
      if (hostsIds.length > 1) {
        return sendCommands({ host_ids: hostsIds, param, action: 'RUN' });
      } else {
        return sendCommand({ host_id: hostsIds[0], param, action: 'RUN' });
      }
    },
    [sendCommand, sendCommands, hostsIds]
  );

  const handleSubmitForm = useCallback<SubmitHandler<IFormValues>>(
    ({ path, title, saved, argumentsCommand }) => {
      if (saved && Boolean(title) && isDirty) {
        if (
          !currentHostCommandTemplate ||
          currentHostCommandTemplate.title !== title
        ) {
          createHostCommandTemplate({
            variables: {
              input: {
                path,
                title,
                arguments: argumentsCommand,
              },
            },
          })
            .then(() => sendCommandHandler(path + argumentsCommand))
            .catch(() => {
              notification.info({
                t,
                type: 'error',
                title: t('host.notify.commands.template.title'),
              });
            })
            .finally(refetchGetHostCommandTemplates);
        } else {
          updateHostCommandTemplate({
            variables: {
              input: {
                id: currentHostCommandTemplate.id,
                path,
                title,
                arguments: argumentsCommand,
              },
            },
          })
            .then(() => sendCommandHandler(path + argumentsCommand))
            .catch(() => {
              notification.info({
                t,
                type: 'error',
                title: t('host.notify.commands.alreadySend.title'),
              });
            })
            .finally(refetchGetHostCommandTemplates);
        }
      } else {
        return sendCommandHandler(path + argumentsCommand);
      }
    },
    [
      currentHostCommandTemplate,
      currentHostCommandTemplate,
      hostsIds,
      sendCommand,
      isDirty,
    ]
  );

  useEffect(() => {
    if (!currentHostCommandTemplate) {
      reset(DEFAULT_FORM_DATA);
    } else {
      reset({
        title: currentHostCommandTemplate.title,
        argumentsCommand: currentHostCommandTemplate.arguments,
        path: currentHostCommandTemplate.path,
        saved: true,
      });
    }
  }, [currentHostCommandTemplate]);

  useEffect(() => {
    if (createCommandError?.message.includes('already exists')) {
      onClose();
    }
  }, [createCommandError]);

  return deletedHostCommandTemplate ? (
    <Modal onClose={closeDeleteModal} className={s.delete}>
      <H2 className={s.delete__title}>
        {t('host_command_template,modal.title')}
      </H2>
      <Paragraph bold color="secondary" className={s.delete__message}>
        {t(
          'host_command_template,modal.message',
          deletedHostCommandTemplate.title
        )}
      </Paragraph>
      <div className={s.delete__buttons}>
        <Button
          variant="outline"
          color="red"
          onClick={() => {
            if (!deletedHostCommandTemplate) return;

            return deleteHostCommandTemplate({
              variables: { id: deletedHostCommandTemplate.id },
            });
          }}
        >
          {t('delete')}
        </Button>
        <Button onClick={closeDeleteModal}>{t('cancel')}</Button>
      </div>
    </Modal>
  ) : (
    <Modal onClose={onClose} disableOutsideClose>
      <div className={s.container}>
        <H2>{t('host.start_command.modal.title')}</H2>
        <div className={s.body}>
          <div className={s.body__templates}>
            <Button
              variant="outline"
              fullWidth
              onClick={clearCurrentHostCommandTemplate}
            >
              {t('host.start_command.modal.new_command.button')}
            </Button>
            <div className={s.templates}>
              <H2 className={s.templates__header}>
                {t('host.start_command.modal.templates')}
              </H2>
              <div className={s.templates__items}>
                <Preloader
                  loading={hostCommandTemplatesLoading}
                  noDataPlaceholder={t(
                    'host.start_command.modal.templates.not_data'
                  )}
                  hasData={
                    hostCommandTemplates &&
                    hostCommandTemplates.hostCommandTemplates?.length > 0
                  }
                  className={s.templates__placeholder}
                >
                  {hostCommandTemplates?.hostCommandTemplates.map(
                    ({ id, title, ...template }) => (
                      <div
                        className={cn(
                          s.template,
                          currentHostCommandTemplate?.id === id && s.active
                        )}
                        key={id}
                        onClick={(event) => {
                          if (
                            !(event.target as HTMLDivElement).closest(
                              `.${s.template__delete}`
                            )
                          ) {
                            setCurrentHostCommandTemplate({
                              ...template,
                              id,
                              title,
                            });
                          }
                        }}
                      >
                        <Paragraph
                          bold
                          className={cn(s.template__name, s.active)}
                        >
                          {truncate(title, { length: 20, separator: '...' })}
                        </Paragraph>
                        <Button
                          variant="ghost"
                          className={s.template__delete}
                          iconName="BucketIcon"
                          onClick={() =>
                            setDeletedHostCommandTemplate({
                              id,
                              title: title || '',
                            })
                          }
                        />
                      </div>
                    )
                  )}
                </Preloader>
              </div>
            </div>
          </div>
          <form className={s.form} onSubmit={handleSubmit(handleSubmitForm)}>
            <RunFileForm
              control={control}
              watch={watch}
              isDirty={isDirty}
              isSubmitting={isSubmitting}
            />
          </form>
        </div>
      </div>
    </Modal>
  );
};
